import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

function StudentResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  });
  const navigate = useNavigate();
  const { token } = useParams();

  // Use environment variable for API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Password strength checker
  const checkPasswordStrength = (password) => {
    const feedback = [];
    let score = 0;

    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push('At least 8 characters');
    }

    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('At least one lowercase letter');
    }

    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('At least one uppercase letter');
    }

    if (/\d/.test(password)) {
      score += 1;
    } else {
      feedback.push('At least one number');
    }

    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      score += 1;
    } else {
      feedback.push('At least one special character');
    }

    return { score, feedback };
  };

  useEffect(() => {
    if (password) {
      setPasswordStrength(checkPasswordStrength(password));
    } else {
      setPasswordStrength({ score: 0, feedback: [] });
    }
  }, [password]);

  const getStrengthColor = (score) => {
    if (score < 2) return '#ff4444';
    if (score < 4) return '#ffaa00';
    return '#00aa00';
  };

  const getStrengthText = (score) => {
    if (score < 2) return 'Weak';
    if (score < 4) return 'Medium';
    return 'Strong';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      setIsError(true);
      return;
    }

    if (passwordStrength.score < 3) {
      setMessage('Password is too weak. Please choose a stronger password.');
      setIsError(true);
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      console.log('Sending reset password request with token:', token);
      const response = await axios.post(`${API_BASE_URL}/student/reset-password/${token}`, { 
        password,
        // Send confirm password for additional backend validation
        confirmPassword
      });
      console.log('Backend response:', response.data);
      setMessage(response.data.message);
      setIsError(false);
      setTimeout(() => {
        console.log('Navigating to /student-login');
        navigate('/student-login');
      }, 2000);
    } catch (error) {
      console.error('Error in reset password request:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong.';
      setMessage(errorMessage);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseMessage = () => {
    setMessage('');
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        height: '100vh',
      }}
    >
      <div
        style={{
          flex: '0 0 60%',
          display: isMobile ? 'none' : 'block',
          backgroundImage: 'url("../images/studentloginimg.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div
        style={{
          flex: '0 0 40%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
          padding: '10px',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '400px',
            backgroundColor: '#fff',
            padding: '20px',
            borderRadius: '16px',
            boxShadow: '0 0 20px rgba(0, 0, 0, 0.08)',
            textAlign: 'center',
          }}
        >
          <h3 style={{ marginBottom: '16px', color: 'purple', fontSize: '24px' }}>
            Reset Password
          </h3>
          
          {message && (
            <div
              style={{
                backgroundColor: isError ? '#FFCCCC' : '#F3E8FF',
                color: '#6B21A8',
                fontFamily: "'Roboto', sans-serif",
                padding: '8px',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                maxWidth: '300px',
                width: '100%',
                margin: '0 auto 16px auto',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                animation: 'slideInFromRight 0.5s ease-out',
              }}
            >
              <span>{message}</span>
              <FontAwesomeIcon
                icon={faTimes}
                style={{
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#6B21A8',
                  fontFamily: "'Roboto', sans-serif",
                }}
                onClick={handleCloseMessage}
                aria-label="Close message"
              />
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              alignItems: 'center',
              marginTop: '2rem',
            }}
          >
            {/* New Password Field */}
            <div style={{ position: 'relative', width: '100%', maxWidth: '300px' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  padding: '10px 40px 10px 14px',
                  borderRadius: '12px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  outline: 'none',
                  transition: '0.3s',
                  width: '100%',
                }}
                onFocus={(e) => (e.target.style.boxShadow = '0 0 0 2px #60a5fa')}
                onBlur={(e) => (e.target.style.boxShadow = 'none')}
              />
              <FontAwesomeIcon
                icon={showPassword ? faEyeSlash : faEye}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  color: '#666',
                  fontSize: '14px'
                }}
                onClick={() => setShowPassword(!showPassword)}
              />
            </div>

            {/* Password Strength Indicator */}
            {password && (
              <div style={{ width: '100%', maxWidth: '300px', textAlign: 'left' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px' 
                }}>
                  <span style={{ fontSize: '12px', color: '#666' }}>Password Strength:</span>
                  <span style={{ 
                    fontSize: '12px', 
                    fontWeight: 'bold',
                    color: getStrengthColor(passwordStrength.score) 
                  }}>
                    {getStrengthText(passwordStrength.score)}
                  </span>
                </div>
                <div style={{ 
                  width: '100%', 
                  height: '4px', 
                  backgroundColor: '#e0e0e0', 
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    width: `${(passwordStrength.score / 5) * 100}%`, 
                    height: '100%', 
                    backgroundColor: getStrengthColor(passwordStrength.score),
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                {passwordStrength.feedback.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    {passwordStrength.feedback.map((item, index) => (
                      <div key={index} style={{ 
                        fontSize: '11px', 
                        color: '#ff4444',
                        marginBottom: '2px'
                      }}>
                        • {item}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Confirm Password Field */}
            <div style={{ position: 'relative', width: '100%', maxWidth: '300px' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{
                  padding: '10px 40px 10px 14px',
                  borderRadius: '12px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  outline: 'none',
                  transition: '0.3s',
                  width: '100%',
                }}
                onFocus={(e) => (e.target.style.boxShadow = '0 0 0 2px #60a5fa')}
                onBlur={(e) => (e.target.style.boxShadow = 'none')}
              />
              <FontAwesomeIcon
                icon={showConfirmPassword ? faEyeSlash : faEye}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  color: '#666',
                  fontSize: '14px'
                }}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              />
            </div>

            {/* Password Match Indicator */}
            {confirmPassword && (
              <div style={{ width: '100%', maxWidth: '300px', textAlign: 'left' }}>
                <span style={{ 
                  fontSize: '12px', 
                  color: password === confirmPassword ? '#00aa00' : '#ff4444'
                }}>
                  {password === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                </span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || passwordStrength.score < 3 || password !== confirmPassword}
              style={{
                padding: '5px 35px',
                backgroundImage: (loading || passwordStrength.score < 3 || password !== confirmPassword)
                  ? 'linear-gradient(#ccc, #ccc), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)'
                  : 'linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                color: 'purple',
                fontWeight: 'bold',
                fontSize: '14px',
                border: '2px solid transparent',
                borderRadius: '30px',
                cursor: (loading || passwordStrength.score < 3 || password !== confirmPassword) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                marginTop: '12px',
                width: '60%',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}
              onMouseOver={(e) => {
                if (!loading && passwordStrength.score >= 3 && password === confirmPassword) {
                  e.target.style.transform = 'scale(1.03)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseOut={(e) => {
                if (!loading && passwordStrength.score >= 3 && password === confirmPassword) {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              {loading ? 'Submitting...' : 'Set New Password'}
            </button>
          </form>
        </div>
      </div>
      <style>
        {`
          @keyframes slideInFromRight {
            0% {
              transform: translateX(100%);
              opacity: 0;
            }
            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
}

export default StudentResetPassword;